import React, { useState, useEffect, useMemo } from 'react';
import { fundAPI } from '../../services/api';
import { Plus, Edit, Trash2, Check, X, Loader2, Clock, RefreshCw } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const fmtCurrency = (value, currency) => {
  const num = Number(value || 0);
  const locale = currency === 'INR' ? 'en-IN' : 'en-US';
  return num.toLocaleString(locale, { maximumFractionDigits: 2, minimumFractionDigits: 2 });
};

const todayStr = () => new Date().toISOString().slice(0, 10);

const FundNAVTab = ({ fundId, fundCurrency, canEdit, canCreate, canDelete }) => {
  const { user } = useAuth();
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';

  const [navs, setNavs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingNav, setEditingNav] = useState(null); // existing nav row when editing
  const [formDate, setFormDate] = useState(todayStr());
  const [formClasses, setFormClasses] = useState([]); // [{class_name, units_issued, nav_per_unit}]
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [error, setError] = useState('');
  const [cascadeMsg, setCascadeMsg] = useState('');

  const fetchNavs = async () => {
    setLoading(true);
    try {
      const res = await fundAPI.getNAVs(fundId);
      setNavs(res.data || []);
    } catch (err) {
      console.error('Failed to load Fund NAVs:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNavs();
  }, [fundId]);

  const { pendingNavs, approvedNavs } = useMemo(() => {
    const pending = navs.filter((n) => n.pending_action === 'update');
    const all = [...navs];
    return { pendingNavs: pending, approvedNavs: all };
  }, [navs]);

  const loadSnapshot = async (date) => {
    if (!date) return;
    setSnapshotLoading(true);
    setError('');
    try {
      const res = await fundAPI.getNAVUnitsSnapshot(fundId, date);
      const classes = (res.data?.classes || []).map((c) => ({
        class_name: c.class_name,
        units_issued: c.units_issued || 0,
        nav_per_unit: 0,
      }));
      setFormClasses(classes);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load class snapshot');
      setFormClasses([]);
    } finally {
      setSnapshotLoading(false);
    }
  };

  const openCreate = () => {
    setEditingNav(null);
    setFormDate(todayStr());
    setFormClasses([]);
    setError('');
    setCascadeMsg('');
    setShowForm(true);
    loadSnapshot(todayStr());
  };

  const openEdit = (nav) => {
    setEditingNav(nav);
    // Use proposed_* if any, else live values
    const date = nav.proposed_date_of_nav || nav.date_of_nav;
    const classes = (nav.proposed_classes || nav.classes || []).map((c) => ({
      class_name: c.class_name,
      units_issued: c.units_issued || 0,
      nav_per_unit: c.nav_per_unit || 0,
    }));
    setFormDate(date);
    setFormClasses(classes);
    setError('');
    setCascadeMsg('');
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingNav(null);
    setFormClasses([]);
    setError('');
  };

  const updateClassField = (idx, field, value) => {
    setFormClasses((prev) => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [field]: value };
      return copy;
    });
  };

  const computedTotal = useMemo(
    () =>
      formClasses.reduce(
        (sum, c) => sum + Number(c.units_issued || 0) * Number(c.nav_per_unit || 0),
        0
      ),
    [formClasses]
  );

  const handleSave = async () => {
    if (!formDate) {
      setError('Date of NAV is required');
      return;
    }
    if (!formClasses.length) {
      setError('No classes to record. Adjust the as-on date.');
      return;
    }
    setSaving(true);
    setError('');
    setCascadeMsg('');
    try {
      const payload = {
        date_of_nav: formDate,
        classes: formClasses.map((c) => ({
          class_name: c.class_name,
          units_issued: Number(c.units_issued || 0),
          nav_per_unit: Number(c.nav_per_unit || 0),
        })),
      };
      let res;
      if (editingNav) {
        res = await fundAPI.updateNAV(fundId, editingNav.id, payload);
        setCascadeMsg('Update submitted for approval.');
      } else {
        res = await fundAPI.createNAV(fundId, payload);
        const c = res.data?._cascaded_account_navs;
        if (typeof c === 'number') {
          setCascadeMsg(`NAV created — cascaded to ${c} investor account${c === 1 ? '' : 's'}.`);
        } else {
          setCascadeMsg('NAV created.');
        }
      }
      await fetchNavs();
      setShowForm(false);
      setEditingNav(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save NAV');
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async (nav) => {
    setActionLoading(`approve-${nav.id}`);
    setCascadeMsg('');
    try {
      const res = await fundAPI.approveNAV(fundId, nav.id);
      const c = res.data?._cascaded_account_navs;
      if (typeof c === 'number') {
        setCascadeMsg(`Update approved — cascaded to ${c} investor account${c === 1 ? '' : 's'}.`);
      }
      await fetchNavs();
    } catch (err) {
      alert(err.response?.data?.detail || 'Approve failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (nav) => {
    if (!window.confirm('Reject this pending NAV change? The current live NAV will be retained.')) return;
    setActionLoading(`reject-${nav.id}`);
    try {
      await fundAPI.rejectNAV(fundId, nav.id);
      await fetchNavs();
    } catch (err) {
      alert(err.response?.data?.detail || 'Reject failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (nav) => {
    if (
      !window.confirm(
        `Delete this NAV entry for ${nav.date_of_nav}? All cascaded Investor Account NAV rows for this date will also be removed.`
      )
    )
      return;
    setActionLoading(`delete-${nav.id}`);
    try {
      await fundAPI.deleteNAV(fundId, nav.id);
      await fetchNavs();
    } catch (err) {
      alert(err.response?.data?.detail || 'Delete failed');
    } finally {
      setActionLoading(null);
    }
  };

  const canApprove = (nav) => {
    // Same maker-checker rule as backend: not own work unless admin
    if (nav.pending_by_user_id && user?.id === nav.pending_by_user_id && !isAdmin) {
      return false;
    }
    return true;
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <Loader2 className="inline-block animate-spin text-gray-400" size={32} />
      </div>
    );
  }

  return (
    <div data-testid="fund-nav-tab">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div>
          <h3 className="text-xl font-semibold">Fund NAV</h3>
          <p className="text-sm text-gray-600 mt-1">
            Maintain per-class per-unit NAV at the fund level. Changes cascade automatically to every
            Investor Account holding this fund (override-aware).
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchNavs}
            className="px-3 py-2 text-sm border rounded-lg text-gray-600 hover:bg-gray-50 flex items-center"
            data-testid="fund-nav-refresh"
          >
            <RefreshCw size={14} className="mr-1" /> Refresh
          </button>
          {canCreate && (
            <button
              onClick={openCreate}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
              data-testid="fund-nav-add"
            >
              <Plus size={16} className="mr-1" /> Add NAV
            </button>
          )}
        </div>
      </div>

      {cascadeMsg && (
        <div className="mb-3 px-3 py-2 bg-green-50 border border-green-200 text-green-800 text-sm rounded">
          {cascadeMsg}
        </div>
      )}

      {/* Pending Approval section */}
      {pendingNavs.length > 0 && (
        <div className="mb-6 border border-amber-300 bg-amber-50 rounded-lg p-4" data-testid="fund-nav-pending-section">
          <div className="flex items-center gap-2 mb-3">
            <Clock size={18} className="text-amber-600" />
            <h4 className="font-semibold text-amber-900">
              Pending Approval ({pendingNavs.length})
            </h4>
          </div>
          <div className="space-y-3">
            {pendingNavs.map((nav) => {
              const proposedClasses = nav.proposed_classes || [];
              const proposedDate = nav.proposed_date_of_nav;
              const proposedTotal = proposedClasses.reduce(
                (s, c) => s + (Number(c.total_value) || Number(c.units_issued || 0) * Number(c.nav_per_unit || 0)),
                0
              );
              return (
                <div key={nav.id} className="bg-white border border-amber-200 rounded p-3" data-testid={`fund-nav-pending-${nav.id}`}>
                  <div className="flex justify-between items-start gap-4 flex-wrap">
                    <div className="flex-1 min-w-[260px]">
                      <div className="text-sm text-gray-600">
                        Submitted by{' '}
                        <span className="font-medium text-gray-900">
                          {nav.pending_by_user_email || nav.pending_by_user_id || 'unknown'}
                        </span>
                        {nav.pending_at && (
                          <> on {new Date(nav.pending_at).toLocaleString()}</>
                        )}
                      </div>
                      <div className="mt-1 text-sm">
                        <span className="text-gray-500">Live date:</span>{' '}
                        <span className="font-medium">{nav.date_of_nav}</span>
                        {proposedDate && proposedDate !== nav.date_of_nav && (
                          <>
                            {' '}<span className="text-amber-700">→ {proposedDate}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {canEdit && canApprove(nav) && (
                        <button
                          onClick={() => handleApprove(nav)}
                          disabled={actionLoading === `approve-${nav.id}`}
                          className="px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-60 flex items-center"
                          data-testid={`fund-nav-approve-${nav.id}`}
                        >
                          {actionLoading === `approve-${nav.id}` ? (
                            <Loader2 size={14} className="animate-spin mr-1" />
                          ) : (
                            <Check size={14} className="mr-1" />
                          )}
                          Approve
                        </button>
                      )}
                      {canEdit && canApprove(nav) && (
                        <button
                          onClick={() => handleReject(nav)}
                          disabled={actionLoading === `reject-${nav.id}`}
                          className="px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-60 flex items-center"
                          data-testid={`fund-nav-reject-${nav.id}`}
                        >
                          <X size={14} className="mr-1" /> Reject
                        </button>
                      )}
                      {canEdit && !canApprove(nav) && (
                        <span className="text-xs italic text-gray-500 self-center">
                          Awaiting a different user to approve
                        </span>
                      )}
                    </div>
                  </div>
                  {/* Proposed classes table */}
                  {proposedClasses.length > 0 && (
                    <div className="mt-3 overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-amber-100">
                          <tr>
                            <th className="px-3 py-1.5 text-left font-medium text-gray-700">Class</th>
                            <th className="px-3 py-1.5 text-right font-medium text-gray-700">Units</th>
                            <th className="px-3 py-1.5 text-right font-medium text-gray-700">Proposed NAV / Unit</th>
                            <th className="px-3 py-1.5 text-right font-medium text-gray-700">Total Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {proposedClasses.map((c) => {
                            const tv = Number(c.total_value) || Number(c.units_issued || 0) * Number(c.nav_per_unit || 0);
                            return (
                              <tr key={c.class_name} className="border-t border-amber-100">
                                <td className="px-3 py-1.5">{c.class_name}</td>
                                <td className="px-3 py-1.5 text-right">{fmtCurrency(c.units_issued, fundCurrency)}</td>
                                <td className="px-3 py-1.5 text-right">{fmtCurrency(c.nav_per_unit, fundCurrency)}</td>
                                <td className="px-3 py-1.5 text-right font-medium">{fmtCurrency(tv, fundCurrency)}</td>
                              </tr>
                            );
                          })}
                          <tr className="border-t border-amber-200 font-semibold bg-amber-50">
                            <td className="px-3 py-1.5" colSpan={3}>Total Proposed</td>
                            <td className="px-3 py-1.5 text-right">
                              {fundCurrency} {fmtCurrency(proposedTotal, fundCurrency)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* NAV list */}
      {approvedNavs.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg" data-testid="fund-nav-empty">
          <p className="text-gray-500">No NAV entries yet for this fund.</p>
          {canCreate && (
            <button
              onClick={openCreate}
              className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 inline-flex items-center"
            >
              <Plus size={16} className="mr-1" /> Add First NAV
            </button>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto border rounded-lg" data-testid="fund-nav-table">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-2.5 text-left font-medium text-gray-700">Date of NAV</th>
                <th className="px-4 py-2.5 text-left font-medium text-gray-700">Classes</th>
                <th className="px-4 py-2.5 text-right font-medium text-gray-700">Total Fund Value</th>
                <th className="px-4 py-2.5 text-left font-medium text-gray-700">Status</th>
                <th className="px-4 py-2.5 text-right font-medium text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {approvedNavs.map((nav) => (
                <tr key={nav.id} className="border-b hover:bg-gray-50" data-testid={`fund-nav-row-${nav.id}`}>
                  <td className="px-4 py-2.5 font-medium">{nav.date_of_nav}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex flex-wrap gap-1">
                      {(nav.classes || []).map((c) => (
                        <span
                          key={c.class_name}
                          className="px-2 py-0.5 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded"
                          title={`${c.units_issued} units @ ${c.nav_per_unit}`}
                        >
                          {c.class_name}: {fmtCurrency(c.nav_per_unit, fundCurrency)}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-right font-medium">
                    {nav.currency || fundCurrency} {fmtCurrency(nav.total_value, fundCurrency)}
                  </td>
                  <td className="px-4 py-2.5">
                    {nav.pending_action === 'update' ? (
                      <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-800 rounded font-medium">
                        Pending Approval
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded font-medium">
                        Active
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="flex justify-end gap-1.5">
                      {canEdit && nav.pending_action !== 'update' && (
                        <button
                          onClick={() => openEdit(nav)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                          title="Edit (requires approval)"
                          data-testid={`fund-nav-edit-${nav.id}`}
                        >
                          <Edit size={14} />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          onClick={() => handleDelete(nav)}
                          disabled={actionLoading === `delete-${nav.id}`}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded disabled:opacity-60"
                          title="Delete (also removes cascaded account NAVs)"
                          data-testid={`fund-nav-delete-${nav.id}`}
                        >
                          {actionLoading === `delete-${nav.id}` ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <Trash2 size={14} />
                          )}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="fund-nav-modal">
          <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-semibold">
                {editingNav ? 'Edit Fund NAV (Requires Approval)' : 'Add Fund NAV'}
              </h3>
              <button onClick={closeForm} className="text-gray-500 hover:text-gray-700">
                <X size={20} />
              </button>
            </div>
            <div className="p-6">
              {error && (
                <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded">
                  {error}
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date of NAV
                </label>
                <input
                  type="date"
                  value={formDate}
                  onChange={(e) => {
                    setFormDate(e.target.value);
                    if (!editingNav) loadSnapshot(e.target.value);
                  }}
                  className="w-full px-3 py-2 border rounded-lg"
                  data-testid="fund-nav-form-date"
                />
                {!editingNav && (
                  <p className="text-xs text-gray-500 mt-1">
                    Units per class are auto-populated from transactions on/before this date.
                  </p>
                )}
              </div>

              <div className="mb-2 flex justify-between items-center">
                <h4 className="font-medium text-gray-800">Class-wise NAV</h4>
                {snapshotLoading && (
                  <span className="text-xs text-gray-500 flex items-center">
                    <Loader2 size={12} className="animate-spin mr-1" /> Loading units…
                  </span>
                )}
              </div>

              {formClasses.length === 0 ? (
                <div className="px-4 py-6 text-center bg-gray-50 rounded text-sm text-gray-500">
                  No classes found for this fund as of the selected date.
                </div>
              ) : (
                <div className="border rounded overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Class</th>
                        <th className="px-3 py-2 text-right font-medium text-gray-700">Units Issued</th>
                        <th className="px-3 py-2 text-right font-medium text-gray-700">NAV / Unit</th>
                        <th className="px-3 py-2 text-right font-medium text-gray-700">Total Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {formClasses.map((c, idx) => {
                        const tv = Number(c.units_issued || 0) * Number(c.nav_per_unit || 0);
                        return (
                          <tr key={c.class_name} className="border-b last:border-0">
                            <td className="px-3 py-2 font-medium">{c.class_name}</td>
                            <td className="px-3 py-2">
                              <input
                                type="number"
                                step="0.0001"
                                value={c.units_issued}
                                onChange={(e) => updateClassField(idx, 'units_issued', e.target.value)}
                                className="w-full px-2 py-1 border rounded text-right"
                                data-testid={`fund-nav-form-units-${idx}`}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="number"
                                step="0.0001"
                                value={c.nav_per_unit}
                                onChange={(e) => updateClassField(idx, 'nav_per_unit', e.target.value)}
                                className="w-full px-2 py-1 border rounded text-right"
                                data-testid={`fund-nav-form-navpu-${idx}`}
                              />
                            </td>
                            <td className="px-3 py-2 text-right font-medium">
                              {fmtCurrency(tv, fundCurrency)}
                            </td>
                          </tr>
                        );
                      })}
                      <tr className="bg-gray-50 font-semibold">
                        <td className="px-3 py-2" colSpan={3}>Total Fund NAV</td>
                        <td className="px-3 py-2 text-right">
                          {fundCurrency} {fmtCurrency(computedTotal, fundCurrency)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              {editingNav && (
                <p className="text-xs text-amber-700 mt-3 bg-amber-50 p-2 rounded border border-amber-200">
                  Edits to Fund NAV require a second user (Maker-Checker) approval before they cascade to
                  Investor Account NAVs. Investor Account NAVs marked as overridden remain untouched.
                </p>
              )}
            </div>
            <div className="px-6 py-3 border-t bg-gray-50 flex justify-end gap-2">
              <button
                onClick={closeForm}
                className="px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !formClasses.length}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60 flex items-center"
                data-testid="fund-nav-form-save"
              >
                {saving && <Loader2 size={14} className="animate-spin mr-1" />}
                {editingNav ? 'Submit for Approval' : 'Create NAV'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FundNAVTab;
